import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import { IconType } from 'components/Icon';
import ShareModal from 'components/Modal/ShareModal';
import Button, { ButtonState, ButtonType } from 'components/Widget/Button/Button';
import ExportDropdown from 'components/Widget/ExportDropdown';
import ExportMenuItem from 'components/Widget/ExportMenuItem';
import {
	Navbar,
	NavbarButton,
	NavbarCenter,
	NavbarLeft,
	NavbarRight,
	NavbarTitle,
} from 'components/Widget/Navbar';
import { DEFAULT_DS_TITLE } from 'dragonmouse-shared/constants';
import { interceptIfNoCredits } from 'economy';
import { useCallback, useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import { selectActiveDesignSystem, selectActiveDesignSystemVersion } from 'redux/db/reducers';
import { setDsTitle } from 'redux/db/actions';
import { designSystemEditingBlocked } from 'redux/db/util';
import { showExportDsAiModal } from 'redux/mode/actions';
import { executeCommand } from 'redux/mode/commandManager';
import { UpdateDesignSystemCommand } from 'redux/mode/commands';
import { selectActiveDsTab } from 'redux/mode/reducers';
import {
	displayConfirmModal,
	displayDsReadOnlyModal,
	displayPublishDsModal,
} from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import * as routes from 'routes';
import DsModeDropdown from './DsModeDropdown';
import Search from './Search';
import * as S from './styles';
import VersionDropdown from './VersionDropdown';

// Width thresholds based on window.innerWidth
const WIDTH_SEARCH_ICON = 1000;
const WIDTH_SHARE_ICON = 900;
const WIDTH_SEARCH_HIDDEN = 700;
const WIDTH_DOWNLOAD_HIDDEN = 600;

const TopBar = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const {
		activeDesignSystem,
		activeDesignSystemId,
		activeDesignSystemVersion,
		isPreparing,
		isMigrating,
		historicalDsVersionNumber,
		isReadOnly,
		dsTitle,
		isDsVersionLoaded,
	} = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeDesignSystemId: r.mode.activeDesignSystemId,
			activeDesignSystemVersion: selectActiveDesignSystemVersion(r.db),
			isPreparing: r.db.dsPreparing,
			isMigrating: r.db.dsMigrating,
			historicalDsVersionNumber: r.mode.historicalDsVersionNumber,
			isReadOnly: r.db.loading.activeDesignSystemVersion === 'loaded' && !r.db.dsCanWrite,
			dsTitle: r.db.dsTitle,
			isDsVersionLoaded: r.db.loading.activeDesignSystemVersion === 'loaded',
		}),
		shallowEqual
	);
	const [showShareModal, setShowShareModal] = useState(false);

	// Auto-open share modal when ?share=open is in the URL
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('share') === 'open') {
			setShowShareModal(true);
			params.delete('share');
			const newUrl = params.toString()
				? `${window.location.pathname}?${params}`
				: window.location.pathname;
			window.history.replaceState({}, '', newUrl);
		}
	}, []);

	const [isRenaming, setIsRenaming] = useState(false);
	const renameRef = useRef<HTMLSpanElement | null>(null);
	const renameValueRef = useRef('');
	const [windowWidth, setWindowWidth] = useState(
		typeof window !== 'undefined' ? window.innerWidth : 1200
	);

	useEffect(() => {
		const onResize = () => {
			setWindowWidth(window.innerWidth);
		};
		window.addEventListener('resize', onResize);
		return () => window.removeEventListener('resize', onResize);
	}, []);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const onDuplicate = useCallback(() => {
		if (!activeDesignSystemId) return;
		const sourceVersion = activeDesignSystemVersion?.versionNumber;
		if (sourceVersion == null) return;
		displayConfirmModal({
			title: 'Duplicate design system',
			description: 'This will create your own editable copy of this design system.',
			submitLabel: 'Duplicate',
			onSubmit: () => {
				const promise = (async () => {
					const res = await forge.duplicateDesignSystem({
						sourceDesignSystemId: activeDesignSystemId,
						sourceVersionNumber: sourceVersion,
					});
					trackDesignSystemAction('duplicate', { source: 'readonly_topbar' });
					navigate(routes.designSystem(res.designSystem.id, res.designSystem.title));
				})();
				toast.promise(promise, {
					pending: 'Duplicating design system...',
					success: 'Design system duplicated',
					error: 'Failed to duplicate design system',
				});
			},
		});
	}, [activeDesignSystemId, activeDesignSystemVersion?.versionNumber, navigate]);

	const onTitleClick = () => {
		if (!activeDesignSystem || isReadOnly) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		renameValueRef.current = dsTitle || DEFAULT_DS_TITLE;
		setIsRenaming(true);
	};

	const onRenameMount = useCallback((el: HTMLSpanElement | null) => {
		renameRef.current = el;
		if (!el) return;
		el.textContent = renameValueRef.current;
		el.focus();
		const range = document.createRange();
		range.selectNodeContents(el);
		const sel = window.getSelection();
		sel?.removeAllRanges();
		sel?.addRange(range);
	}, []);

	const onRenameInput = () => {
		renameValueRef.current = renameRef.current?.textContent ?? '';
	};

	const onRenameSubmit = () => {
		const trimmed = renameValueRef.current.trim();
		setIsRenaming(false);
		if (!activeDesignSystem || !trimmed) return;
		const originalTitle = dsTitle || DEFAULT_DS_TITLE;
		if (trimmed !== originalTitle) {
			trackDesignSystemAction('rename');
			// Optimistic update for the title bar (which reads r.db.dsTitle, not the optimistic DS write).
			dispatch(setDsTitle(trimmed));
			executeCommand(
				new UpdateDesignSystemCommand(
					{ id: activeDesignSystem.id, title: originalTitle },
					{ id: activeDesignSystem.id, title: trimmed }
				)
			);
		}
	};

	const onRenameKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			onRenameSubmit();
		} else if (e.key === 'Escape') {
			setIsRenaming(false);
		}
	};

	const onExportAi = () => {
		trackDesignSystemAction('open_export_ds_ai_modal');
		const tab = selectActiveDsTab(store.getState().mode);
		if (tab?.section === 'components' && tab.contentPath) {
			// tab.contentPath is the prefixed tag name (e.g. "ds-button"); modal keys on bare dir name.
			const componentDir = tab.contentPath.replace(/^ds-/, '');
			dispatch(
				showExportDsAiModal({
					initialPreset: 'components',
					initialComponentNames: [componentDir],
				})
			);
		} else if (tab?.section === 'theme' || tab?.section === 'styles') {
			dispatch(showExportDsAiModal({ initialPreset: 'tokens' }));
		} else {
			dispatch(showExportDsAiModal({ initialPreset: 'all' }));
		}
	};

	const isDraft = activeDesignSystemVersion?.versionNumber === 0 && !isPreparing;
	const isViewingHistorical = historicalDsVersionNumber !== null;

	const searchMode =
		windowWidth < WIDTH_SEARCH_HIDDEN
			? 'hidden'
			: windowWidth < WIDTH_SEARCH_ICON
			? 'iconOnly'
			: 'full';
	const showShareText = windowWidth >= WIDTH_SHARE_ICON;
	const showDownload = windowWidth >= WIDTH_DOWNLOAD_HIDDEN;

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const onPublish = () => {
		if (!activeDesignSystemId) return;
		if (isMigrating) return;
		const blocked = designSystemEditingBlocked();
		if (blocked) {
			displayDsReadOnlyModal(blocked);
			return;
		}
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: activeDesignSystemId })) {
			return;
		}
		displayPublishDsModal(activeDesignSystemId, dsTitle);
	};

	return (
		<>
			<Navbar layout="left-aligned">
				<NavbarLeft>
					<S.TitleWrapper onClick={isRenaming ? undefined : onTitleClick}>
						{isRenaming ? (
							<S.RenameInput
								ref={onRenameMount}
								contentEditable
								suppressContentEditableWarning
								onInput={onRenameInput}
								onBlur={onRenameSubmit}
								onKeyDown={onRenameKeyDown}
							/>
						) : (
							<NavbarTitle>
								{isDsVersionLoaded ? dsTitle || DEFAULT_DS_TITLE : '\u00A0'}
							</NavbarTitle>
						)}
					</S.TitleWrapper>
					<VersionDropdown />
					<DsModeDropdown />
				</NavbarLeft>

				{isReadOnly && (
					<NavbarCenter>
						<S.ReadOnlyBanner>
							Viewing · <S.ReadOnlyLink onClick={onDuplicate}>Duplicate to edit</S.ReadOnlyLink>
						</S.ReadOnlyBanner>
					</NavbarCenter>
				)}

				{!isPreparing && (
					<NavbarRight>
						<Search compactMode={searchMode} />
						{showDownload && (
							<S.Download>
								<ExportDropdown>
									<ExportMenuItem
										icon={IconType.GENERATE}
										label="Export for AI"
										description=".MD for Claude Code, ChatGPT, Codex…"
										onSelect={onExportAi}
									/>
								</ExportDropdown>
							</S.Download>
						)}
						{!isReadOnly && (
							<S.PublishButton>
								<Button
									onSelected={onPublish}
									type={ButtonType.PUBLISH}
									state={
										isDraft && !isViewingHistorical ? ButtonState.REGULAR : ButtonState.DISABLED
									}
								>
									Publish
								</Button>
							</S.PublishButton>
						)}
						<S.Share>
							{showShareText ? (
								<Button
									onSelected={() => {
										trackDesignSystemAction('open_share_modal');
										setShowShareModal(true);
									}}
									type={ButtonType.NOTICEABLE}
								>
									Share
								</Button>
							) : (
								<NavbarButton
									icon={IconType.SHARE}
									onClick={() => {
										trackDesignSystemAction('open_share_modal');
										setShowShareModal(true);
									}}
									tooltip="Share"
									tooltipId="share-design-system-tooltip"
								/>
							)}
						</S.Share>
					</NavbarRight>
				)}
			</Navbar>
			{showShareModal && activeDesignSystem && (
				<ShareModal
					kind="designSystem"
					entityId={activeDesignSystem.id}
					onClose={() => setShowShareModal(false)}
				/>
			)}
		</>
	);
};

export default TopBar;
